import * as Api from '@ViewModels';

/**
 * Mapping of full US state names to their two-letter abbreviations
 */
const STATE_ABBREVIATIONS: Record<string, string> = {
	Alabama: 'AL',
	Alaska: 'AK',
	Arizona: 'AZ',
	Arkansas: 'AR',
	California: 'CA',
	Colorado: 'CO',
	Connecticut: 'CT',
	Delaware: 'DE',
	Florida: 'FL',
	Georgia: 'GA',
	Hawaii: 'HI',
	Idaho: 'ID',
	Illinois: 'IL',
	Indiana: 'IN',
	Iowa: 'IA',
	Kansas: 'KS',
	Kentucky: 'KY',
	Louisiana: 'LA',
	Maine: 'ME',
	Maryland: 'MD',
	Massachusetts: 'MA',
	Michigan: 'MI',
	Minnesota: 'MN',
	Mississippi: 'MS',
	Missouri: 'MO',
	Montana: 'MT',
	Nebraska: 'NE',
	Nevada: 'NV',
	'New Hampshire': 'NH',
	'New Jersey': 'NJ',
	'New Mexico': 'NM',
	'New York': 'NY',
	'North Carolina': 'NC',
	'North Dakota': 'ND',
	Ohio: 'OH',
	Oklahoma: 'OK',
	Oregon: 'OR',
	Pennsylvania: 'PA',
	'Rhode Island': 'RI',
	'South Carolina': 'SC',
	'South Dakota': 'SD',
	Tennessee: 'TN',
	Texas: 'TX',
	Utah: 'UT',
	Vermont: 'VT',
	Virginia: 'VA',
	Washington: 'WA',
	'West Virginia': 'WV',
	Wisconsin: 'WI',
	Wyoming: 'WY',
	'District of Columbia': 'DC',
	'Puerto Rico': 'PR',
	Guam: 'GU',
	'American Samoa': 'AS',
	'U.S. Virgin Islands': 'VI',
	'Northern Mariana Islands': 'MP',
};

/**
 * Converts a full state name to its two-letter abbreviation.
 * If the input is already an abbreviation or not found in the lookup, returns the original value.
 *
 * @param stateName - The full state name or abbreviation
 * @returns The two-letter state abbreviation
 *
 * @example
 * getStateAbbreviation('North Carolina')
 * // Returns: "NC"
 *
 * @example
 * getStateAbbreviation('NC')
 * // Returns: "NC"
 */
const getStateAbbreviation = (stateName: string): string => {
	return STATE_ABBREVIATIONS[stateName] || stateName;
};

/**
 * Type for objects that have a storefront address property
 */
type WithStorefrontAddress = {
	storefrontAddress?: Api.IGoogleStorefrontAddress;
};

/**
 * Type for objects that have location description and storefront address
 */
type WithLocationInfo = WithStorefrontAddress & {
	locationDescription?: string;
};

/**
 * Formats a Google Business location's address into a readable string.
 * Returns the locality (city) and administrative area (state) separated by a comma.
 *
 * @param location - An object with a storefrontAddress property (e.g., IGoogleBusinessLocation or IGoogleBusinessReview)
 * @returns A formatted address string like "Austin, TX" or null if no address data is available
 *
 * @example
 * formatLocationAddress({ storefrontAddress: { locality: 'Austin', administrativeArea: 'TX' } })
 * // Returns: "Austin, TX"
 *
 * @example
 * formatLocationAddress({ storefrontAddress: { locality: 'Austin' } })
 * // Returns: "Austin"
 *
 * @example
 * formatLocationAddress({ storefrontAddress: null })
 * // Returns: null
 */
export const formatLocationAddress = (location: WithStorefrontAddress): string | null => {
	if (!location.storefrontAddress) {
		return null;
	}

	const { locality, administrativeArea } = location.storefrontAddress;

	if (!locality && !administrativeArea) {
		return null;
	}

	const parts = [];
	if (locality) {
		parts.push(locality);
	}
	if (administrativeArea) {
		parts.push(getStateAbbreviation(administrativeArea));
	}

	return parts.join(', ');
};

/**
 * Formats a Google Business location's full information into a readable string.
 * Combines the location description and address (locality/state) with the address in parentheses.
 *
 * @param location - An object with locationDescription and storefrontAddress properties
 * @returns A formatted location string like "Location Name (Austin, TX)" or null if no location data is available
 *
 * @example
 * formatLocation({ locationDescription: 'Downtown Store', storefrontAddress: { locality: 'Austin', administrativeArea: 'TX' } })
 * // Returns: "Downtown Store (Austin, TX)"
 *
 * @example
 * formatLocation({ locationDescription: 'Downtown Store' })
 * // Returns: "Downtown Store"
 *
 * @example
 * formatLocation({ storefrontAddress: { locality: 'Austin', administrativeArea: 'TX' } })
 * // Returns: "Austin, TX"
 *
 * @example
 * formatLocation({ storefrontAddress: null })
 * // Returns: null
 */
export const formatLocation = (location: WithLocationInfo): string | null => {
	const locationDescription = location.locationDescription;
	const address = formatLocationAddress(location);

	// If we have both, use parentheses around the address
	if (locationDescription && address) {
		return `${locationDescription} (${address})`;
	}

	// Otherwise return whichever one we have
	return locationDescription || address || null;
};
